import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ImageCropperInput } from '../Components/ImageCropperInput';
import CustomButton from '../Components/CustomButton';
import Images from '../Utils/images';

// Updated Validation Schema with improved error messages
const schema = yup.object().shape({
  documentName: yup.string().required('Document Name is required'),
  //   documentUrl: yup.string().test({
  //     name: 'documentUrlValidation',
  //     test: function (value, context) {
  //       const { hasUploadedFile } = this.options.context || {};
  //       const trimmedValue = value?.trim();

  //       // If a file is uploaded, URL is not required
  //       if (hasUploadedFile) {
  //         return true;
  //       }

  //       // If no file is uploaded and no URL is provided
  //       if (!trimmedValue) {
  //         return this.createError({
  //           message: 'Either upload a document or provide a document URL',
  //           path: 'documentUrl',
  //         });
  //       }

  //       // Validate URL format if provided
  //       if (!yup.string().url().isValidSync(trimmedValue)) {
  //         return this.createError({
  //           message: 'Please enter a valid URL',
  //           path: 'documentUrl',
  //         });
  //       }

  //       return true;
  //     },
  //   }),
  documentUrl: yup.string().test({
    name: 'documentUrlValidation',
    test: function (value, context) {
      const { hasUploadedFile } = this.options.context || {};
      const trimmedValue = value?.trim();

      // If a file is uploaded, URL is not required
      if (hasUploadedFile) {
        return true;
      }

      // If no file is uploaded and no URL is provided
      if (!trimmedValue) {
        return this.createError({
          message: 'Either upload a document or provide a document URL',
          path: 'documentUrl',
        });
      }

      // Validate URL format if provided
      if (!yup.string().url().isValidSync(trimmedValue)) {
        return this.createError({
          message: 'Please enter a valid URL',
          path: 'documentUrl',
        });
      }

      return true;
    },
  }),
});

interface UploadNewDocumentProps {
  open: boolean;
  handleClose: () => void;

  selectedDeptId?: string | any;
  onAddDocument?: any;
}

const CreateUploadDoc: React.FC<UploadNewDocumentProps> = ({
  open,
  handleClose,
  onAddDocument,
  selectedDeptId,
}) => {
  const [load, setLoad] = useState(false);
  const [document, setDocument] = useState('');
  const [documents, setDocuments] = useState<any>({});
  const [documentType, setDocumentType] = useState('');
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    reset,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      documentName: '',
      documentUrl: '',
    },
    context: { hasUploadedFile },
    mode: 'onChange', // Validate on change
  });

  const watchDocumentUrl = watch('documentUrl');
  const watchDocumentName = watch('documentName');

  // Revalidate the form when hasUploadedFile changes
  useEffect(() => {
    trigger();
  }, [hasUploadedFile, trigger]);

  // Helper function to determine if we should show field errors
  const shouldShowError = fieldName => {
    return (
      (touchedFields[fieldName] || dirtyFields[fieldName] || isSubmitted) &&
      errors[fieldName]
    );
  };

  const handleAddDocument = (docDetails: {
    deptId: string;
    docName: string;
    type: string;
    url: string;
  }) => {
    setDocuments(docDetails);
    // Store the document type
    setDocumentType(docDetails.type || '');
    // Set document URL
    setDocument(docDetails.url || '');
    // Update the uploaded file state
    setHasUploadedFile(true);
    // Don't prefill the document name anymore - let user type it
    // Clear document URL since we have a file now
    setValue('documentUrl', '', { shouldValidate: true });
  };

  const handleUploadDocument = formData => {
    if (isProcessing) return;
    setIsSubmitted(true);

    // Manual validation to ensure either file or URL is provided
    if (
      !hasUploadedFile &&
      (!formData.documentUrl || formData.documentUrl.trim() === '')
    ) {
      setValue('documentUrl', formData.documentUrl, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }
    setIsProcessing(true);
    onAddDocument({
      deptId: selectedDeptId, // Set this dynamically based on your application logic
      docName: formData.documentName,
      docFormat: documentType || documents?.type,
      documentUrl: document || formData.documentUrl,
    });
    handleClose();
  };

  // Function to clear the document
  const clearDocument = () => {
    setDocument('');
    setDocumentType('');
    setHasUploadedFile(false);
    setValue('documentName', '', { shouldValidate: true });
  };

  // Function to reset the form
  const resetForm = () => {
    reset({
      documentName: '',
      documentUrl: '',
    });
    setDocument('');
    setDocumentType('');
    setHasUploadedFile(false);
    setDocuments({});
    setIsSubmitted(false);
  };

  // Function to determine if the document is a PDF
  const isPdf = () => {
    if (document) {
      return document.toLowerCase().endsWith('.pdf');
    }
    return false;
  };

  // Function to determine if the document is an Excel file
  const isExcel = () => {
    if (document) {
      const lowerCaseDoc = document.toLowerCase();
      return (
        lowerCaseDoc.endsWith('.xlsx') ||
        lowerCaseDoc.endsWith('.xls') ||
        lowerCaseDoc.endsWith('.csv')
      );
    }
    return false;
  };

  // Function to render the preview based on document type
  const renderPreview = () => {
    if (isPdf()) {
      // For PDF files, show the PDF icon
      return <img src={Images.PDF} alt="PDF Document" />;
    } else if (isExcel()) {
      // For Excel files, show the Excel icon
      return <img src={Images.EXCEL} alt="Excel Document" />;
    }
    // For other image files, show the image itself
    return <img src={document} alt="Preview" />;
  };

  // Function to check if the form is valid for submission
  const isFormValid = () => {
    // Form is valid if:
    // 1. Selected department exists
    // 2. Document name is filled
    // 3. AND either a document is uploaded OR document URL is provided
    return (
      selectedDeptId &&
      watchDocumentName &&
      watchDocumentName.trim() !== '' &&
      (hasUploadedFile || (watchDocumentUrl && watchDocumentUrl.trim() !== ''))
    );
  };

  // Handle modal close with form reset
  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleModalClose} className="modal-main">
        <div className="modal-dialog med">
          <div className="modal-content">
            <div className="modal-title d-flex flex-between align-center">
              <p className="title text-gray_700 fw500">Upload Documents</p>
              <button className="blank-btn" onClick={handleModalClose}>
                <i className="cross-icon"></i>
              </button>
            </div>
            <div className="modal-body pt-15 pb-15">
              <div className="body-scroll-sm pt-12">
                <div className="imageContainer flex-column mb-24">
                  {!document && (
                    <ImageCropperInput
                      setLoad={setLoad}
                      setUrl={setDocument}
                      loading={load}
                      ratio={2 / 1}
                      title="Add Document"
                      type="Document"
                      onlyDocs
                      onAddDocument={handleAddDocument}
                      guideline={true}
                      disabled={Boolean(
                        watchDocumentUrl && watchDocumentUrl.trim().length > 0,
                      )}
                      setFileName={setValue}
                    />
                  )}

                  {document && (
                    <div className="imageItem">
                      <Box className="selectimg_video">
                        <IconButton
                          className="removeImage"
                          onClick={clearDocument}
                        >
                          <CloseIcon />
                        </IconButton>

                        <figure className="selected">{renderPreview()}</figure>
                      </Box>
                    </div>
                  )}
                  <p className="text-gray_500  MuiTypography-subtitle3">
                    You can only upload files in JPG, JPEG, PNG, and PDF formats
                  </p>
                </div>

                <form onSubmit={handleSubmit(handleUploadDocument)}>
                  <FormControl
                    className="form-in mb-24"
                    fullWidth
                    variant="outlined"
                  >
                    <label className="mb-8 MuiTypography-subtitle1">
                      Document Name <span style={{ color: 'red' }}>*</span>
                    </label>
                    <TextField
                      size="small"
                      type="text"
                      placeholder="Add document name"
                      variant="outlined"
                      {...register('documentName')}
                      error={shouldShowError('documentName')}
                      helperText={
                        shouldShowError('documentName')
                          ? errors.documentName?.message
                          : ''
                      }
                      fullWidth
                    />
                  </FormControl>
                  <FormControl
                    className="form-in mb-24"
                    fullWidth
                    variant="outlined"
                  >
                    <label className="mb-8 MuiTypography-subtitle1">
                      Document URL{' '}
                      {!hasUploadedFile && (
                        <span style={{ color: 'red' }}>*</span>
                      )}
                    </label>
                    <TextField
                      size="small"
                      type="text"
                      placeholder="Add Document URL"
                      variant="outlined"
                      {...register('documentUrl')}
                      error={shouldShowError('documentUrl')}
                      helperText={
                        shouldShowError('documentUrl')
                          ? errors.documentUrl?.message
                          : ''
                      }
                      fullWidth
                      disabled={hasUploadedFile}
                    />
                  </FormControl>

                  {/* Error message if no department is selected - only show after submission */}
                  {isSubmitted && !selectedDeptId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ display: 'block', mb: 2 }}
                    >
                      Please select a department before uploading a document
                    </Typography>
                  )}
                </form>
              </div>
              <div className="modal-actions">
                <div>
                  <CustomButton
                    id=""
                    text="Cancel"
                    size="medium"
                    variant="outlined"
                    showIcon={false}
                    onClick={handleModalClose}
                    className="min_width_btn"
                  />
                </div>

                <div>
                  <CustomButton
                    id=""
                    text="Upload"
                    type="submit"
                    size="medium"
                    variant="contained"
                    showIcon={false}
                    className="min_width_btn"
                    onClick={handleSubmit(handleUploadDocument)}
                    disabled={!isFormValid() || isProcessing}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateUploadDoc;
